import { useMemo } from 'react';
import { useTranslate } from 'ra-core';
import { type CsvRecord, type GridRow } from './types';

interface BulkOperationDialogGridFooterProps<T extends CsvRecord = CsvRecord> {
  gridData: GridRow<T>[];
}

export function BulkOperationDialogGridFooter<T extends CsvRecord>({
  gridData,
}: BulkOperationDialogGridFooterProps<T>) {
  const translate = useTranslate();
  const invalidRecords = useMemo(
    () => gridData.filter((d) => d.status === 'error'),
    [gridData]
  );

  const validationProgress = useMemo(() => {
    let successCount = 0,
      errorCount = 0,
      processingCount = 0;

    for (const row of gridData) {
      switch (row.status) {
        case 'success':
          successCount++;
          break;
        case 'error':
          errorCount++;
          break;
        default:
          processingCount++;
      }
    }

    return {
      total: gridData.length,
      successCount,
      errorCount,
      processingCount,
    };
  }, [gridData]);

  return validationProgress.processingCount > 0 ? (
    <p className="shrink-0">
      <b>
        {translate('moonstar.bulk_operation.validation.validating', {
          _: 'Validating...',
        })}
      </b>{' '}
      <span>
        ({validationProgress.successCount + validationProgress.errorCount}/
        {validationProgress.total})
      </span>
    </p>
  ) : (
    <p className="shrink-0">
      <span>
        <b>
          {translate('moonstar.bulk_operation.validation.total', {
            _: 'Total:',
          })}{' '}
        </b>
        <span>{validationProgress.total}</span>
      </span>
      {validationProgress.errorCount > 0 ? (
        <span>
          ,{' '}
          <b>
            {translate('moonstar.bulk_operation.validation.invalid', {
              _: 'Invalid:',
            })}{' '}
          </b>
          <span>{validationProgress.errorCount}</span>{' '}
          <span>
            (
            {translate('moonstar.bulk_operation.validation.row', {
              _: 'row',
            })}{' '}
            {invalidRecords
              .slice(0, 5)
              .map((r) => r.id + 1)
              .join(', ')}
            {invalidRecords.length > 5 ? '...' : ''})
          </span>
        </span>
      ) : null}
    </p>
  );
}
